import {
  Component,
  signal,
  output,
  computed,
  Input,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { AuthForm } from '../../types/auth-form.type';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CtaButtonComponent } from '../../../../shared/components/ui/cta-button/cta-button.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { strongPasswordValidator } from '../../../../core/validators/strong-password.validator';
import { COUNTRIES } from '../../../../shared/constants/countries';
import { combineLatest, startWith } from 'rxjs';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';

@Component({
  selector: 'app-signup-form',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ReactiveFormsModule,
    InputFieldComponent,
    CtaButtonComponent,
    ParentButtonComponent,
    MatStepperModule,
    // SocialSigningsComponent,
  ],
  templateUrl: './signup-form.component.html',
  styleUrl: './signup-form.component.scss',
})
export class SignupFormComponent {
  #takeUntilDestroyed = getTakeUntilDestroyed();
  #requestPending = signal(false);
  #layout = signal<string | undefined>(undefined);
  @ViewChild('signupStepper', { static: false }) private stepper?: MatStepper;

  isLoading = computed(() => this.#requestPending());
  readonly isParentLayout = computed(() => this.#layout() === 'parent');

  @Input() set requestPending(v: boolean) {
    this.#requestPending.set(v);
  }

  @Input() set layout(value: string | undefined) {
    this.#layout.set(value);
  }

  public signupForm!: FormGroup<AuthForm>;
  public emailValidation = signal<Record<string, string>>({
    required: '',
    email: '',
  });
  public nameValidation = signal<Record<string, string>>({
    required: '',
    userName: '',
  });
  public passwordValidation = signal<Record<string, string>>({
    required: '',
  });
  public firstNameValidation = signal<Record<string, string>>({
    required: '',
  });
  public lastNameValidation = signal<Record<string, string>>({
    required: '',
  });
  public countryValidation = signal<Record<string, string>>({
    required: '',
  });
  public userNameValidation = signal<Record<string, string>>({
    required: '',
    userName: '',
  });

  public countries = signal<Array<{ label: string; value: string }>>(COUNTRIES);

  public formSubmitted = output<Record<string, string | null | undefined>>();

  constructor(private translate: TranslateService, private fb: FormBuilder) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadValidationMessages();
    this.setupAutoUsername();
  }

  public onSubmit(): void {
    if (this.signupForm.valid) {
      this.formSubmitted.emit(this.signupForm.getRawValue());
    }
  }

  /** Shows API username errors on the credentials step with optional suggestions. */
  public setUserNameServerError(message: string, suggestions: string[]): void {
    const serverMessage = this.formatUsernameServerError(message, suggestions);

    this.userNameValidation.update((current) => ({
      ...current,
      server: serverMessage,
    }));
    this.signupForm.controls.userName.setErrors({ server: true });
    this.signupForm.controls.userName.markAsTouched();

    if (this.stepper && this.stepper.selectedIndex !== 1) {
      this.stepper.selectedIndex = 1;
    }
  }

  public profileStepValid(): boolean {
    if (!this.signupForm) {
      return false;
    }
    const firstName = this.signupForm.controls.firstName;
    const lastName = this.signupForm.controls.lastName;
    const country = this.signupForm.controls.country;
    const email = this.signupForm.controls.email;

    return Boolean(
      firstName?.valid && lastName?.valid && country?.valid && email?.valid
    );
  }

  public goToCredentialsStep(): void {
    const firstName = this.signupForm.controls.firstName!;
    const lastName = this.signupForm.controls.lastName!;
    const country = this.signupForm.controls.country!;
    const email = this.signupForm.controls.email!;
    firstName.markAsTouched();
    lastName.markAsTouched();
    country.markAsTouched();
    email.markAsTouched();
    if (firstName.valid && lastName.valid && country.valid && email.valid) {
      this.stepper?.next();
    }
  }

  public goBackToProfileStep(): void {
    this.stepper?.previous();
  }

  private initForm(): void {
    this.signupForm = this.fb.group<AuthForm>({
      userName: this.fb.control('', {
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-z0-9._]+$/),
        ],
        nonNullable: true,
      }),
      firstName: this.fb.control('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      lastName: this.fb.control('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      country: this.fb.control('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      email: this.fb.control('', {
        validators: [Validators.required, Validators.email],
        nonNullable: true,
      }),
      password: this.fb.control('', {
        validators: [Validators.required, strongPasswordValidator],
        nonNullable: true,
      }),
    });
  }

  // Tracks the last auto-generated username so we don't fight with user edits
  private lastAutoUsername = '';
  private userNameManuallyEdited = false;

  private setupAutoUsername(): void {
    const firstNameCtrl = this.signupForm.controls.firstName!;
    const lastNameCtrl = this.signupForm.controls.lastName!;
    const userNameCtrl = this.signupForm.controls.userName!;

    // Detect manual edits to userName
    userNameCtrl.valueChanges
      .pipe(this.#takeUntilDestroyed())
      .subscribe((value) => {
        const current = value ?? '';
        // If the value diverges from our last suggestion, treat as manual edit
        if (current !== this.lastAutoUsername) {
          this.userNameManuallyEdited = true;
        }
      });

    // React to first/last name changes and prefill username when not manually edited
    combineLatest([
      firstNameCtrl.valueChanges.pipe(startWith(firstNameCtrl.value)),
      lastNameCtrl.valueChanges.pipe(startWith(lastNameCtrl.value)),
    ])
      .pipe(this.#takeUntilDestroyed())
      .subscribe(([firstName, lastName]) => {
        const suggestion = this.generateUsername(
          firstName ?? '',
          lastName ?? '',
        );
        // Update only if user hasn't manually edited or the control still equals our last suggestion
        const currentUserName = userNameCtrl.value ?? '';
        const safeToUpdate =
          !this.userNameManuallyEdited ||
          currentUserName === this.lastAutoUsername;
        if (safeToUpdate && suggestion !== currentUserName) {
          this.lastAutoUsername = suggestion;
          userNameCtrl.setValue(suggestion, { emitEvent: false });
        }
      });
  }

  private formatUsernameServerError(
    message: string,
    suggestions: string[],
  ): string {
    if (!suggestions.length) {
      return message;
    }

    const maxShown = 5;
    const deduped = this.dedupeUsernameSuggestionsForDisplay(suggestions);
    const shown = deduped.slice(0, maxShown);
    const lines = shown.map((name) => `  • ${name}`);
    if (deduped.length > maxShown) {
      lines.push(`  • …`);
    }

    const tryLabel = this.translate.instant('app.auth.usernameSuggestionsTry');
    return [message, '', tryLabel, ...lines].join('\n');
  }

  /** Prefer `name.2` over `name2` when both are present. */
  private dedupeUsernameSuggestionsForDisplay(suggestions: string[]): string[] {
    const set = new Set(suggestions);
    return suggestions.filter((name) => {
      const match = name.match(/^(.+?)(\d+)$/);
      if (!match || match[1].endsWith('.')) {
        return true;
      }
      return !set.has(`${match[1]}.${match[2]}`);
    });
  }

  private generateUsername(firstName: string, lastName: string): string {
    const clean = (s: string) =>
      s
        .normalize('NFKD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^A-Za-z0-9]+/g, '')
        .toLowerCase();

    const f = clean(firstName);
    const l = clean(lastName);
    const dot = f && l ? '.' : '';
    const base = `${f}${dot}${l}`;
    return base.replace(/[^A-Za-z0-9._]/g, '');
  }

  private loadValidationMessages(): void {
    this.translate
      .get([
        'app.auth.requiredUserName',
        'app.auth.validUserName',
        'app.auth.requiredEmail',
        'app.auth.validEmail',
        'app.auth.requiredPassword',
        'app.auth.requiredFirstName',
        'app.auth.requiredLastName',
        'app.auth.requiredCountry',
      ])
      .pipe(this.#takeUntilDestroyed())
      .subscribe((translations) => {
        this.emailValidation.set({
          required: translations['app.auth.requiredEmail'],
          email: translations['app.auth.validEmail'],
        });
        this.passwordValidation.set({
          required: translations['app.auth.requiredPassword'],
        });
        this.firstNameValidation.set({
          required: translations['app.auth.requiredFirstName'],
        });
        this.lastNameValidation.set({
          required: translations['app.auth.requiredLastName'],
        });
        this.countryValidation.set({
          required: translations['app.auth.requiredCountry'],
        });
        this.userNameValidation.set({
          required: translations['app.auth.requiredUserName'],
          userName: translations['app.auth.validUserName'],
        });
      });
  }
}
